import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { CheckCircle2, RefreshCw, Smartphone } from 'lucide-react';
import { backofficeApi, extractErrorMessage } from '@kardume/services';
import { useNotification } from '@kardume/hooks';
import type { MessagingQrCodeDto } from '@kardume/types';
import { Button, Modal, Spinner } from '@kardume/ui';

const Body = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.9rem;
  text-align: center;
`;

const Steps = styled.ol`
  margin: 0;
  padding-left: 1.15rem;
  align-self: stretch;
  text-align: left;
  font-size: 0.83rem;
  line-height: 1.6;
  color: ${(p) => p.theme.textSecondary};
`;

const QrFrame = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 264px;
  height: 264px;
  padding: 0.6rem;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  background: #fff;

  img {
    width: 100%;
    height: 100%;
    image-rendering: pixelated;
  }
`;

const PairingBox = styled.div`
  align-self: stretch;
  padding: 0.7rem 0.85rem;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surfaceAlt};
  font-size: 0.83rem;
  color: ${(p) => p.theme.textSecondary};

  strong {
    display: block;
    margin-top: 0.25rem;
    font-size: 1.1rem;
    letter-spacing: 0.12em;
    color: ${(p) => p.theme.textPrimary};
  }
`;

const Status = styled.p<{ $connected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  margin: 0;
  font-size: 0.85rem;
  font-weight: 700;
  color: ${(p) => (p.$connected ? p.theme.success : p.theme.textMuted)};
`;

const Expired = styled.p`
  margin: 0;
  font-size: 0.85rem;
  line-height: 1.5;
  color: ${(p) => p.theme.warning};
`;

/**
 * O QR da Evolution expira em poucas dezenas de segundos. Depois disso não vale
 * insistir: a tela para de mostrar um código morto e oferece gerar outro.
 */
const QR_LIFETIME_MS = 55000;

/** Frequência do polling de status. É o nosso próprio servidor; 3s é confortável. */
const POLL_MS = 3000;

interface QrCodeModalProps {
  isOpen: boolean;
  instanceId: string | null;
  instanceLabel: string;
  /** QR já obtido no cadastro - evita uma segunda ida ao provedor ao abrir. */
  initialQrCode?: MessagingQrCodeDto | null;
  onClose: () => void;
  /** Chamado quando o pareamento é confirmado, para a listagem recarregar. */
  onConnected?: () => void;
}

/**
 * Pareamento do número: mostra o QR Code e pergunta ao provedor, de tempos em
 * tempos, se já conectou.
 *
 * O polling é o que fecha o ciclo - sem ele o operador escaneia, nada muda na tela e
 * ele não sabe se funcionou. Ao confirmar a conexão, o polling para: continuar
 * perguntando depois de conectado é só carga no provedor.
 */
const QrCodeModal: React.FC<QrCodeModalProps> = ({
  isOpen,
  instanceId,
  instanceLabel,
  initialQrCode,
  onClose,
  onConnected,
}) => {
  const { showNotification } = useNotification();
  const [qrCode, setQrCode] = useState<MessagingQrCodeDto | null>(initialQrCode ?? null);
  const [issuedAt, setIssuedAt] = useState<number>(() => Date.now());
  const [expired, setExpired] = useState(false);
  const [connected, setConnected] = useState<boolean>(initialQrCode?.alreadyConnected ?? false);
  const [refreshing, setRefreshing] = useState(false);

  // Reabrir o modal para outra instância não pode herdar o QR da anterior.
  useEffect(() => {
    if (!isOpen) return;
    setQrCode(initialQrCode ?? null);
    setConnected(initialQrCode?.alreadyConnected ?? false);
    setIssuedAt(Date.now());
    setExpired(false);
  }, [isOpen, instanceId, initialQrCode]);

  const requestNewQrCode = useCallback(async (): Promise<void> => {
    if (!instanceId) return;
    setRefreshing(true);
    try {
      const result = await backofficeApi.connectMessagingInstance(instanceId);
      setQrCode(result);
      setConnected(result.alreadyConnected);
      setIssuedAt(Date.now());
      setExpired(false);
      if (result.alreadyConnected) onConnected?.();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível gerar o QR Code.'), 'error');
    } finally {
      setRefreshing(false);
    }
  }, [instanceId, onConnected, showNotification]);

  // Marca o QR como expirado em vez de deixar o operador escaneando um código morto.
  useEffect(() => {
    if (!isOpen || connected || !qrCode || expired) return undefined;
    const remaining = QR_LIFETIME_MS - (Date.now() - issuedAt);
    const timer = setTimeout(() => setExpired(true), Math.max(remaining, 0));
    return () => clearTimeout(timer);
  }, [isOpen, connected, qrCode, expired, issuedAt]);

  // Polling do estado real no provedor: é o que confirma que o scan funcionou.
  useEffect(() => {
    if (!isOpen || !instanceId || connected) return undefined;
    const timer = setInterval(async () => {
      try {
        const state = await backofficeApi.getMessagingConnection(instanceId);
        if (state.connected) {
          setConnected(true);
          showNotification('Número conectado.', 'success');
          onConnected?.();
        }
      } catch {
        // Silencioso de propósito: o provedor responde 404 enquanto a instância
        // ainda está subindo, e um toast a cada 3s esconderia a tela inteira.
      }
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [isOpen, instanceId, connected, onConnected, showNotification]);

  const showQr = !connected && !expired && !!qrCode?.base64;
  const showPairing = !connected && !expired && !qrCode?.base64 && !!qrCode?.pairingCode;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Conectar ${instanceLabel}`}
      width="420px"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {connected ? 'Fechar' : 'Depois'}
          </Button>
          {!connected && (
            <Button variant="primary" onClick={requestNewQrCode} isLoading={refreshing}>
              <RefreshCw size={15} /> Gerar novo QR Code
            </Button>
          )}
        </>
      }
    >
      <Body>
        {connected ? (
          <>
            <CheckCircle2 size={44} color="#1E9E6A" />
            <Status $connected>Número conectado</Status>
            <Steps>
              <li>Pronto. Esta instância já pode ser usada nas campanhas.</li>
            </Steps>
          </>
        ) : (
          <>
            <Steps>
              <li>Abra o WhatsApp no celular do número.</li>
              <li>
                Toque em <strong>Configurações → Aparelhos conectados → Conectar aparelho</strong>.
              </li>
              <li>Aponte a câmera para o código abaixo.</li>
            </Steps>

            {showQr && (
              <QrFrame>
                <img src={qrCode?.base64 ?? ''} alt="QR Code para conectar o WhatsApp" />
              </QrFrame>
            )}

            {showPairing && (
              <PairingBox>
                O servidor não devolveu a imagem do QR. Use o código de pareamento em
                &quot;Conectar com número de telefone&quot;:
                <strong>{qrCode?.pairingCode}</strong>
              </PairingBox>
            )}

            {expired && (
              <Expired>
                O código expirou - eles duram menos de um minuto. Gere um novo para escanear.
              </Expired>
            )}

            {/* Resposta sem QR e sem código de pareamento: sem esta linha o modal
                ficava só com as instruções e o "aguardando", sem nada para escanear
                e sem dizer que o botão abaixo é a saída. */}
            {!expired && qrCode && !qrCode.base64 && !qrCode.pairingCode && (
              <Expired>
                O servidor não devolveu o código desta vez. Clique em &quot;Gerar novo QR Code&quot;.
              </Expired>
            )}

            {!qrCode && !expired && <Spinner />}

            <Status>
              <Smartphone size={15} /> Aguardando a leitura no celular…
            </Status>
          </>
        )}
      </Body>
    </Modal>
  );
};

export default QrCodeModal;
